import React from "react";
import PDVlogo from "../../experience/landingpage/header/assets/PDVlogo.svg";
import "./invoice.css";
import "./receipt.css";
import { formatInvoiceMoney } from "./Invoice";
import { getDateString, getTimeString } from "../../utils/dateutils";

/**
 * Maps API `receipt` object to props for `<Receipt data={...} />`.
 */
export function mapRecordToReceiptData(receipt) {
  if (!receipt) return null;

  return {
    accountNumber: receipt?.accountNumber,
    organizationName: receipt?.organizationName,
    address: receipt?.organizationCompleteAddress,
    transactionDate: getDateString(receipt?.transactionDate),
    transactionTime: getTimeString(receipt?.transactionDate),
    invoiceId: receipt?.invoiceId,
    invoiceGeneratedDate: getDateString(receipt?.invoiceGeneratedDate),
    amountPaid: receipt?.amountPaid,
    reference: receipt?.paymentReference,
    method: receipt?.cardBrand,
    codingNumber: receipt?.codingNumber,
    note: `Card ending in ${receipt?.cardLastFourDigits} · ${formatInvoiceMoney(receipt?.amountPaid)} charged by HEAL Project`,
  };
}

function ReceiptSection({ title, children }) {
  return (
    <section className="rcpt-section">
      <h2 className="rcpt-section-title">{title}</h2>
      {children}
    </section>
  );
}

function ReceiptRow({ label, value }) {
  const raw = value == null ? "" : String(value).trim();
  const isEmpty = raw === "" || raw === "—";
  const display = isEmpty ? "Not provided" : value;
  return (
    <div className="rcpt-row">
      <span className="rcpt-row-label">{label}</span>
      <span
        className={`rcpt-row-value${isEmpty ? " rcpt-row-value--placeholder" : ""}`}
      >
        {display}
      </span>
    </div>
  );
}

function Receipt({ data }) {
  const d = data;

  return (
    <div className="rcpt-shell">
      <article className="rcpt-card">
        <header className="inv-header">
          <div className="inv-brand">
            <img src={PDVlogo} alt="" width={120} height={80} />
          </div>
          <div className="inv-org">
            <p className="inv-org-title">Hospice Volunteer Association</p>
            <p className="inv-org-sub">Patient Data Vault Services</p>
          </div>
        </header>

        <div className="inv-account">
          <div className="inv-account-left">
            <p className="inv-account-label">Account Number</p>
            <p className="inv-account-value">{d.accountNumber}</p>
          </div>
          <div>
            <p className="inv-address font-weight-bold">
              {d?.organizationName}
            </p>
            <p className="inv-address">{d?.address}</p>
          </div>
        </div>

        {d?.codingNumber && (
          <div className="inv-account">
            <div>
              <p className="inv-address font-weight-bold">{d?.codingNumber}</p>
            </div>
          </div>
        )}

        <div className="rcpt-title-row">
          <h1 className="rcpt-title">PAYMENT RECEIPT</h1>
        </div>

        <div className="rcpt-summary" aria-label="Transaction summary">
          <div className="rcpt-summary-cell">
            <span className="rcpt-summary-label">Transaction Date</span>
            <span className="rcpt-summary-value">{d.transactionDate}</span>
          </div>
          <div className="rcpt-summary-cell">
            <span className="rcpt-summary-label">Transaction Time</span>
            <span className="rcpt-summary-value">{d.transactionTime}</span>
          </div>
        </div>

        <ReceiptSection title="Payment details">
          <div className="rcpt-rows rcpt-payment-rows">
            <ReceiptRow label="Payment Reference" value={d.reference} />
            <ReceiptRow label="Payment Method" value={d.method} />
            <ReceiptRow label="Payment" value={d.note} />
          </div>
        </ReceiptSection>

        <ReceiptSection title="Invoices covered">
          <div
            className="rcpt-breakdown"
            role="table"
            aria-label="Service charges"
          >
            <div className="rcpt-breakdown-row" role="row">
              <span className="rcpt-breakdown-label" role="cell">
                Invoice Number
              </span>
              <span className="rcpt-breakdown-value" role="cell">
                {d.invoiceId}
              </span>
            </div>
            <div className="rcpt-breakdown-row" role="row">
              <span className="rcpt-breakdown-label" role="cell">
                Invoice Date
              </span>
              <span className="rcpt-breakdown-value" role="cell">
                {d.invoiceGeneratedDate}
              </span>
            </div>
            <div className="rcpt-breakdown-row" role="row">
              <span className="rcpt-breakdown-label" role="cell">
                Amount Paid
              </span>
              <span className="rcpt-breakdown-value" role="cell">
                {formatInvoiceMoney(d.amountPaid)}
              </span>
            </div>
          </div>
        </ReceiptSection>

        <footer className="rcpt-footer">
          <p className="rcpt-footer-support">
            Questions? Contact PDV Services through the PDV Help Desk.
          </p>
          <p className="rcpt-footer-thanks">
            Thank you for your continued support and business.
          </p>
        </footer>
      </article>
    </div>
  );
}

export default Receipt;
